/* eslint-disable @typescript-eslint/no-namespace */
/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AsFn,
  First,
  GetValues,
  InnerJoin,
  Last,
  LeftJoin,
  MakeOptional,
  MergeKeys,
  PickKey,
  SimplifyObj,
  UnionToTuple,
} from '@main/shared/types';

declare const InjectedDepsKey: unique symbol;

export type DepsFn<Data = any, Deps = any, Ret = any> = (data: Data, deps: Deps) => Ret;
export type InferDeps<F> = F extends DepsFn<any, infer D> ? D : unknown;

export interface DepsInjector<Injected extends object, Override extends boolean = false> {
  [InjectedDepsKey]: { injected: Injected; override: Override };
}
export type DepsInjectorFn<
  Injected extends object,
  Override extends boolean = false,
  Data = any,
  Deps = any,
> = DepsFn<Data, Deps> & DepsInjector<Injected, Override>;

export type DepsPipeFns<Fns extends any[]> = Fns & DepsValidation.ValidatePipe<Fns>;

namespace DepsCompute {
  type ValidDepsKeys<Deps, ProvidedDeps> = GetValues<{
    [K in MergeKeys<ProvidedDeps, Deps>]: ProvidedDeps[K] extends Deps[K] ? K : never;
  }>;

  type ParseDeps<
    Deps,
    ProvidedDeps,
    ValidKeys extends MergeKeys<Deps, ProvidedDeps> = ValidDepsKeys<Deps, ProvidedDeps>,
    OtherDeps = Omit<ProvidedDeps, ValidKeys>,
    InvalidDeps = InnerJoin<OtherDeps, Deps>,
  > = {
    ValidKeys: ValidKeys;
    OtherDeps: OtherDeps;
    InvalidDeps: InvalidDeps;
  };

  type CreateInvalidDeps<InvalidDeps> = {
    [K in keyof InvalidDeps]: CreateDepError<K>;
  };

  type CreateDepError<Key> =
    `Error: Invalid dependency "${Key & string}" injected! Injected type does not match required type.`;

  type ComputeDeps<RequiredDeps, ProvidedDeps> =
    ParseDeps<RequiredDeps, ProvidedDeps> extends {
      ValidKeys: infer ValidKeys;
      OtherDeps: infer OtherDeps;
      InvalidDeps: infer InvalidDeps;
    }
      ? MakeOptional<Omit<RequiredDeps, keyof InvalidDeps>, ValidKeys & string> &
          Partial<Omit<OtherDeps, keyof InvalidDeps>> &
          Required<CreateInvalidDeps<InvalidDeps>>
      : never;

  type ComputeErrors<RequiredDeps, ProvidedDeps> =
    ParseDeps<RequiredDeps, ProvidedDeps> extends {
      InvalidDeps: infer InvalidDeps;
    }
      ? Required<CreateInvalidDeps<InvalidDeps>>
      : never;

  export type MergeDeps<ProvidedDeps, InjectedDeps, Override = false> = Override extends true
    ? LeftJoin<InjectedDeps, ProvidedDeps>
    : LeftJoin<ProvidedDeps, InjectedDeps>;

  export type CollectDeps<
    Fns extends any[],
    RequiredDeps = {},
    ProvidedDeps = {},
    Deps = {},
  > = Fns extends [infer Head, ...infer Tail]
    ? Head extends DepsInjector<infer Injected, infer Override>
      ? MergeDeps<ProvidedDeps, Injected, Override> extends infer CurrentProvidedDeps
        ? CollectDeps<
            Tail,
            RequiredDeps,
            CurrentProvidedDeps,
            LeftJoin<Deps, ComputeDeps<RequiredDeps, CurrentProvidedDeps>>
          >
        : never
      : RequiredDeps & InferDeps<Head> extends infer CurrentRequiredDeps
        ? CollectDeps<
            Tail,
            CurrentRequiredDeps,
            ProvidedDeps,
            LeftJoin<ComputeDeps<CurrentRequiredDeps, ProvidedDeps>, Deps>
          >
        : never
    : {
        required: RequiredDeps;
        provided: ProvidedDeps;
        computed: LeftJoin<ComputeErrors<RequiredDeps, ProvidedDeps>, Deps>;
      };

  export type CalculateDeps<Fns extends any[]> =
    CollectDeps<Fns> extends { computed: infer Deps } ? SimplifyObj<Deps> : {};
}

namespace DepsValidation {
  type GetNextProvidedDeps<Fn, ProvidedDeps> =
    Fn extends DepsInjector<infer Injected, infer Override>
      ? DepsCompute.MergeDeps<ProvidedDeps, Injected, Override>
      : ProvidedDeps;

  type GetMismatchKeys<RequiredDeps, ProvidedDeps> = UnionToTuple<
    GetValues<{
      [K in MergeKeys<RequiredDeps, ProvidedDeps>]: ProvidedDeps[K] extends RequiredDeps[K]
        ? never
        : K;
    }>
  >;

  type ValidateFn<Fn, Input, ProvidedDeps, RequiredDeps> =
    Fn extends DepsInjectorFn<infer Injected, infer Override, any, infer Deps>
      ? GetMismatchKeys<RequiredDeps, ProvidedDeps> extends [any, ...any] & [...infer InvalidKeys]
        ? DepsInjectorFn<
            CreateExpectedDeps<InvalidKeys[number] & string, RequiredDeps, Injected>,
            Override,
            Input,
            Deps
          >
        : DepsInjectorFn<Injected, Override, Input, Deps>
      : Fn extends DepsFn<any, infer Deps, infer Ret>
        ? DepsFn<Input, Deps, Ret>
        : Fn;

  type CreateExpectedDeps<
    InvalidKeys extends PropertyKey,
    RequiredDeps,
    ProvidedDeps,
  > = SimplifyObj<LeftJoin<{ [K in InvalidKeys]: PickKey<RequiredDeps, K> }, ProvidedDeps>>;

  export type ValidatePipe<
    Fns extends any[],
    Input = any,
    ProvidedDeps = {},
    RequiredDeps = DepsCompute.CollectDeps<Fns>['required'],
  > = Fns extends [infer Head, ...infer Tail]
    ? GetNextProvidedDeps<Head, ProvidedDeps> extends infer NextProvidedDeps
      ? [
          ValidateFn<Head, Input, NextProvidedDeps, RequiredDeps>,
          ...ValidatePipe<Tail, ReturnType<AsFn<Head>>, NextProvidedDeps, RequiredDeps>,
        ]
      : Fns
    : Fns;
}

export namespace Deps {
  export function pipe<Fns extends DepsFn[]>(...fns: DepsPipeFns<Fns>) {
    type Deps = DepsCompute.CalculateDeps<Fns>;

    return (
      data: Parameters<First<Fns>>[0],
      ...[deps]: {} extends Deps ? [deps?: Deps] : [deps: Deps]
    ) => {
      deps = { ...deps } as Deps;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return fns.reduce((acc, fn) => fn(acc, deps), data) as ReturnType<Last<Fns>>;
    };
  }

  export function flow<Fns extends DepsFn[]>(
    data: Parameters<First<Fns>>[0],
    deps: DepsCompute.CalculateDeps<Fns>,
    ...fns: DepsPipeFns<Fns>
  ): ReturnType<Last<Fns>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return
    return pipe(...fns)(data as never, deps);
  }

  export function injectDeps<InjectedDeps extends object, Data, Deps extends object>(
    fn: DepsFn<Data, Deps, InjectedDeps>,
  ): DepsInjectorFn<InjectedDeps, true, Data, Deps>;
  export function injectDeps<InjectedDeps extends object>(
    injectedDeps: InjectedDeps,
  ): DepsInjectorFn<InjectedDeps, false>;
  export function injectDeps<
    InjectedDeps extends object,
    Override extends boolean,
    Data,
    Deps extends object,
  >(
    depsOrFn: InjectedDeps | DepsFn<Data, Deps, InjectedDeps>,
  ): DepsInjectorFn<InjectedDeps, Override, Data, Deps> {
    return ((data, deps) => {
      const shouldOverride = typeof depsOrFn === 'function';
      const injectedDeps = typeof depsOrFn === 'function' ? depsOrFn(data, deps) : depsOrFn;

      for (const [key, value] of Object.entries(injectedDeps)) {
        if (shouldOverride || key in deps === false) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          (deps as any)[key] = value;
        }
      }

      return data;
    }) as DepsInjectorFn<InjectedDeps, Override, Data, Deps>;
  }

  export function identity<D>() {
    return (data: D) => data;
  }
}
